import { isEmail } from 'class-validator';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { WLIDS } from 'Constants';
import useMutation from 'Hooks/useMutation';
import { addStandby, AddStandbyRequestDto } from 'Services/Api/_HumanWritten/StandbyServiceApi';
import alertToast from 'Util/ToastifyUtils';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { useQueryClient } from '@tanstack/react-query';

export interface StandbyValues {
	name?: string;
	email?: string;
	phone?: string;
	userId?: string;
}

interface UseStandbyFormArgs {
	ferryTripIds: string[];
	onCompletion: () => void;
	defaultValues?: StandbyValues;
	/** Used from check-in. Only labels the trip history row. */
	inCheckIn?: boolean;
}

export const useStandbyForm = ({ ferryTripIds, onCompletion, defaultValues, inCheckIn }: UseStandbyFormArgs) => {
	const [name, setName] = useState<string | null>(defaultValues?.name ?? null);
	const [email, setEmail] = useState<string | null>(defaultValues?.email ?? null);
	const [phone, setPhone] = useState<string | null>(defaultValues?.phone ?? null);
	const [userId, setUserId] = useState<string | null>(defaultValues?.userId ?? null);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const queryClient = useQueryClient();

	const addRequest = useMutation((request: AddStandbyRequestDto) => {
		return addStandby(request);
	});

	const onSubmit = async (e: React.FormEvent<HTMLFormElement> | any, onComplete?: () => void) => {
		e?.preventDefault();
		setErrors({});

		const newErrors: Record<string, string> = {};

		if (!!!name) {
			newErrors.name = 'Required';
		}

		if (!!!email) {
			newErrors.email = 'Required';
		} else if (!isEmail(email ?? '')) {
			newErrors.email = 'Enter a valid email';
		}

		if (!!!phone) {
			newErrors.phone = 'Required';
		} else if (!IsValidPhone(phone)) {
			newErrors.phone = 'Enter a valid phone number';
		}

		if (Object.entries(newErrors).length > 0) {
			setErrors(newErrors);
		} else {
			const addStandbyDto: AddStandbyRequestDto = {
				name: name!,
				email: email!,
				phone: phone!,
				ferryTripIds,
				userId,
				inCheckIn,
			};
			try {
				const result = await addRequest.mutateAsync(addStandbyDto);
				if (result.code === 'StandbyMaxCountExceeded') {
					alertToast(
						null,
						'error',
						<FormattedMessage id={WLIDS.standby.listIsFull} defaultMessage="Standby list is full" />,
					);
					return;
				}
				alertToast(
					<FormattedMessage id={WLIDS.standby.addedToast} defaultMessage="Added to standby" />,
					'success',
				);
				await queryClient.invalidateQueries({ queryKey: ['standby', ferryTripIds[0]] });
				onCompletion();
			} catch (err) {
				console.error(err);
				alertToast(
					<FormattedMessage id={WLIDS.standby.addFailedToast} defaultMessage="Failed to add to standby" />,
					'error',
				);
			}
		}
	};

	return {
		name,
		setName,
		email,
		setEmail,
		phone,
		setPhone,
		userId,
		setUserId,
		errors,
		setErrors,
		addRequest,
		onSubmit,
	};
};
